import React, { useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import {
  Assignable,
  ProjectElementType,
  Removable,
  event as eventMock,
} from "@planzone/dto";
import { UseCases } from "@planzone/core";
import { useAssignTag, useEditEvent } from "@planzone/view-models";
import { useModal } from "@root/client/modal";
import { CommentsCount } from "../commentsCount";
import { AttachmentsCount } from "../attachmentsCount";
import { EventIcon } from "@common/eventIcon";
import { EditIcon } from "@common/editIcon";
import { TrashIcon } from "@common/trashIcon";
import { DateForm } from "./dateForm";
import { ActivityWatcher } from "@common/activityWatcher";
import { TransferIcon } from "@common/transferIcon";
import { SecureContextualMenu } from "@common/secureContextualMenu";
import { SecondaryCriteriaForm } from "@common/secondaryCriteriaForm";
import { SecondaryCriteria } from "../secondaryCriteria";
import { Tags } from "../tags";
import { Context } from "../context";
import { useProjectCtx } from "../projectCtx";
import { Props } from "./types";
import { NameEditionForm } from "./nameEditionForm";
import styles from "./styles.scss";

function Event({ event, meta, parentList, minDate, maxDate }: Props) {
  const realEvent = { ...eventMock, ...event };
  const { t } = useTranslation();
  const { pushView } = useModal();
  const { project, template } = useProjectCtx();
  const projectTimezone = project.timeZone || DateTime.local().zoneName;
  const { hoveredElement } = React.useContext(Context);
  const { isRequestPending, editEvent } = useEditEvent(realEvent);
  const [showContextualMenu, setShowContextualMenu] = useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  const { assignTag } = useAssignTag();
  const isDone =
    DateTime.fromISO(event.endDate, { zone: "utc" }) < DateTime.now();

  ActivityWatcher.useActivityWatcher(ref, {
    focus: () => setShowContextualMenu(!showContextualMenu),
    blur: () => setShowContextualMenu(false),
  });

  return (
    <div
      className={classNames(styles.event, {
        [styles.done]: isDone,
        [styles.hovered]:
          !!hoveredElement && hoveredElement.uuid === event.uuid,
      })}
    >
      <SecondaryCriteria project={project}>
        {() => ({
          main: (
            <div
              className={styles.eventDetails}
              onClick={() =>
                pushView({
                  key: UseCases.retrieveEvent,
                  data: { event: realEvent },
                })
              }
            >
              <div className={styles.name}>
                <div
                  className={styles.eventCompletionStatus}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <EventIcon className={styles.icon} />
                </div>
                <NameEditionForm
                  event={realEvent}
                  hovered={
                    !!hoveredElement && hoveredElement.uuid === event.uuid
                  }
                />
              </div>
            </div>
          ),
          criteria: {
            tags: (
              <div className={styles.tags}>
                <Tags
                  tags={event.tags}
                  project={project}
                  removable={Removable.EVENT}
                  uuid={event.uuid}
                  add={(tag) =>
                    assignTag({
                      tag,
                      assignable: Assignable.EVENT,
                      assignableId: event.uuid,
                      project,
                    })
                  }
                />
              </div>
            ),
            endDate: (
              <DateForm
                event={realEvent}
                value={
                  event.endDate
                    ? (DateTime.fromISO(event.endDate, { zone: "utc" })
                        .toUTC()
                        .toISO() ?? "")
                    : ""
                }
                projectTimezone={projectTimezone}
                disabled={isRequestPending}
                minDate={minDate}
                maxDate={maxDate}
                submit={(date) => {
                  const nextDt = DateTime.fromISO(date, { zone: "utc" });
                  const nextIso = nextDt.toUTC().toISO() ?? "";
                  const currentDt = event.endDate
                    ? DateTime.fromISO(event.endDate, { zone: "utc" })
                    : null;
                  editEvent({
                    event: realEvent,
                    nextEvent: {
                      ...realEvent,
                      endTime:
                        !!currentDt && nextDt.equals(currentDt) ? "" : nextIso,
                    },
                  });
                }}
              />
            ),
            ...template.reduce(
              (acc, criteria) => ({
                ...acc,
                [criteria.uuid]: (
                  <SecondaryCriteriaForm
                    criteria={criteria}
                    value={
                      event.attributes
                        ? event.attributes[criteria.uuid] || ""
                        : ""
                    }
                    projectTimezone={projectTimezone}
                    disabled={isRequestPending}
                    submit={(value) =>
                      editEvent({
                        event: realEvent,
                        nextEvent: {
                          ...realEvent,
                          attributes: {
                            ...event.attributes,
                            [criteria.uuid]: value,
                          },
                        },
                      })
                    }
                  />
                ),
              }),
              {},
            ),
          },
          comments: (
            <CommentsCount
              subject={event.uuid}
              type="event"
              commentsNbr={meta.commentCount}
            />
          ),
          files: (
            <AttachmentsCount
              uuid={event.uuid}
              fileNbr={meta.attachmentCount}
            />
          ),
          contextualMenu: (
            <div
              className={classNames(styles.contextualMenu, {
                [styles.displayContextualMenu]: showContextualMenu,
              })}
            >
              <div ref={ref}>
                <SecureContextualMenu
                  context={{ event: realEvent }}
                  replacement={<div className={styles.placeholder} />}
                  items={[
                    {
                      name: t("listsList.contextualMenu.edit"),
                      useCase: UseCases.editEvent,
                      renderIcon: (className) => (
                        <EditIcon className={className} />
                      ),
                      act: () =>
                        pushView({
                          data: { event: realEvent },
                          key: UseCases.retrieveEvent,
                        }),
                    },
                    {
                      name: t("listsList.contextualMenu.move"),
                      useCase: UseCases.moveListElement,
                      renderIcon: (className) => (
                        <TransferIcon className={className} />
                      ),
                      act: () =>
                        pushView({
                          key: UseCases.moveListElement,
                          data: {
                            projectElementId: event.uuid,
                            projectElementType: ProjectElementType.EVENT,
                            currentParentList: parentList,
                            project,
                          },
                        }),
                    },
                    {
                      name: t("listsList.contextualMenu.remove"),
                      useCase: UseCases.removeEvent,
                      renderIcon: (className) => (
                        <TrashIcon
                          className={classNames(className, styles.removeIcon)}
                        />
                      ),
                      color: "#db5f63",
                      act: () =>
                        pushView({
                          data: realEvent,
                          key: UseCases.removeEvent,
                        }),
                    },
                  ]}
                />
              </div>
            </div>
          ),
        })}
      </SecondaryCriteria>
    </div>
  );
}

export { Event };
